import cx from "classnames";
import * as React from "react";
import { useIsOverflow } from "../utility/useIsOverflow";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { JSX } from "react";

export enum CheckboxStateEnum {
  Unchecked = 1,
  Indeterminate,
  Checked,
}

export interface QESCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * A ref to be passed to the checkbox itself
   */
  checkboxRef?: React.RefObject<HTMLInputElement>;
  /**
   * Controls whether the checkbox is checked
   */
  checked: boolean;
  /**
   * The class to assign to the outer container around the label, input, and descriptive text
   */
  containerClassName?: string;
  /**
   * Controls whether the checkbox is indeterminate. This takes priority over checked.
   */
  indeterminate?: boolean;
  /**
   *  The label by the checkbox
   */
  label: string | JSX.Element;
  /**
  * An event to trigger when any label is clicked, just in case you want it to do something 
  * different or in addition to the normal onChange handler. If this is just the same as the 
  * onChange handler, you risk it being triggered twice.
  */
  onLabelClick?: (e: React.MouseEvent) => void;
  /**
   * Whether the field is required. This mostly determines the error vs warning text
   */
  required?: boolean;
  /**
   *  Used for the QA automated test handle, and as the element's unique id
   */
  testId: string;
  /**
   * Used to set the checkbox border red.
   */
  isBorderRed?: boolean;
  /**
   * Display label text in tooltip if text overflows container.
   */
  showTooltipOnOverflow?: boolean;
}

/**
 * It's a checkbox. Most of the customization is in the styling.
 */
/* We call out the custom props and manually used props here individually, so ...props doesn't overwrite anything or add weird stuff */
export const QESCheckbox: React.FC<React.PropsWithChildren<QESCheckboxProps>> = ({ checkboxRef, checked = false, className, containerClassName, id, indeterminate = false, isBorderRed, label, onLabelClick, prefix, required, testId, showTooltipOnOverflow, ...props }) => {
  const prefixedId = !!id ? (prefix ?? "") + id : id;
  const prefixedTestId = !!testId ? (prefix ?? "") + testId : testId;

  const labelRef = React.useRef<HTMLLabelElement>(null);
  const showTooltip = showTooltipOnOverflow && useIsOverflow(labelRef);

  return (
    <div className={cx({
      [containerClassName]: !!containerClassName,
      "qes-checkbox__container": true,
    })}
    >
      <TooltipWrapper text={showTooltip ? label : ""} triggerWidth="100%">
        <input
          type="checkbox"
          className={cx({
            [className]: !!className,
            "qes-checkbox": true,
            "red-border": isBorderRed,
          })}
          id={prefixedId || prefixedTestId}
          // the qesRef prop was replaced here for the sake of the indeterminate state (it was unused). In the event we need an additional ref here later, see https://reactjs.org/docs/hooks-reference.html#useimperativehandle
          // or we can add a ref to the props list and just return it from this function. I'm not sure which is preferred.
          ref={(el) => { el && (el.indeterminate = indeterminate); checkboxRef && (checkboxRef.current = el); }}
          required={required}
          data-testid={prefixedTestId}
          checked={checked}
          {...props}
        />
        <label className={`qes-checkbox__label${props.disabled ? " disabled" : ""}`} htmlFor={prefixedId || prefixedTestId} onClick={onLabelClick} ref={labelRef}>
          {label}{required ? <span className="text-danger"> *</span> : ""}
        </label>
      </TooltipWrapper>
    </div>
  );
};
